import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import { JotformEmbed } from '@share/components';
import { bookingManualSliceActions } from '@share/store/slices';
import { NULL_VALUE } from '@constants';

import { SUPPLIER_REGISTRATION_JOTFORM_SRC } from '../../../dashboard/constants';

import { AccountManualUpload } from './manual-upload';
import { AccountWebsite } from './website';

import styles from './styles.module.scss';

type AgentTool = 'website' | 'manualUpload' | 'supplier';

interface IProps {
  tool: AgentTool;
}

const INTRO_IDS: Record<Exclude<AgentTool, 'website'>, string> = {
  manualUpload: 'my.account.hub.agent.manual_upload_intro',
  supplier: 'my.account.hub.agent.supplier_intro',
};

/**
 * My Dashboard's agent tools inside the hub: the same components the dashboard tabs render, in a hub
 * card. Their behaviour and data are unchanged.
 */
export const AccountAgentTool = ({ tool }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Same reset My Dashboard runs when its Manual Upload tab opens, so an old submit result isn't shown.
  useEffect(() => {
    if (tool === 'manualUpload') {
      dispatch(bookingManualSliceActions.setPostManualBookingLoading(false));
      dispatch(bookingManualSliceActions.setPostManualBookingError(''));
      dispatch(bookingManualSliceActions.setPostManualBooking(NULL_VALUE));
    }
  }, [dispatch, tool]);

  // My website has its own hub layout; the other tools keep My Dashboard's component in a hub card.
  if (tool === 'website') {
    return <AccountWebsite />;
  }
  if (tool === 'manualUpload') {
    return <AccountManualUpload />;
  }

  return (
    <div className={styles.agentTool}>
      <p className={styles.intro}>{intl.formatMessage({ id: INTRO_IDS[tool] })}</p>
      <section className={tool === 'supplier' ? `${styles.card} ${styles.cardEmbed}` : styles.card}>
        {tool === 'supplier' ? <JotformEmbed src={SUPPLIER_REGISTRATION_JOTFORM_SRC} frameBorder="0" /> : null}
      </section>
    </div>
  );
};
